import pick from "lodash/pick";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { createWithEqualityFn } from "zustand/traditional";

import { createApolloClientSlice } from "./slices/apolloClient.slice";
import { createCurrentIntervalSlice } from "./slices/currentInterval.slice";
import { createCurrentSiteSlice } from "./slices/currentSite.slice";
import { createDataEntryDateSlice } from "./slices/dataEntryDate.slice";
import { createDataEntryViewSlice } from "./slices/dataEntryView.slice";
import { createDataPointsSlice } from "./slices/dataPoints.slice";
import { createDevEnvBannerSlice } from "./slices/devEnvBannerSlice";
import { createDevModeSlice } from "./slices/devMode.slice";
import { createDirtyDataPointsSlice } from "./slices/dirtyDataPoints.slice";
import { createDrawerSlice } from "./slices/drawer.slice";
import { createEditModeSlice } from "./slices/editMode.slice";
import { createEnvSlice } from "./slices/env.slice";
import { createHiddenAndArchivedModesSlice } from "./slices/hiddenAndArchived.slice";
import { createLoggedInUserSlice } from "./slices/loggedInUser.slice";
import { createNotificationsSlice } from "./slices/notifications.slice";
import { createOnlineStatusSlice } from "./slices/onlineStatus.slice";
import { createOverviewSystemFilterSlice } from "./slices/overviewStateFilter.slice";
import { createPerSiteLocalPrefsSlice } from "./slices/perSiteLocalPrefs.slice";
import { createReportingDiscoverySlice } from "./slices/reportingDiscovery.slice";
import { createRoundsAssetsMappingsSlice } from "./slices/roundsAssetsMappings.slice";
import { createSubscriptionClientIdSlice } from "./slices/subscriptionClientId.slice";
import { createWindowFocusSlice } from "./slices/windowFocus.slice";

export type CombinedSlices = ReturnType<typeof createDataEntryDateSlice> &
  ReturnType<typeof createSubscriptionClientIdSlice> &
  ReturnType<typeof createEditModeSlice> &
  ReturnType<typeof createDevModeSlice> &
  ReturnType<typeof createDataEntryViewSlice> &
  ReturnType<typeof createCurrentIntervalSlice> &
  ReturnType<typeof createDataPointsSlice> &
  ReturnType<typeof createApolloClientSlice> &
  ReturnType<typeof createLoggedInUserSlice> &
  ReturnType<typeof createDirtyDataPointsSlice> &
  ReturnType<typeof createPerSiteLocalPrefsSlice> &
  ReturnType<typeof createHiddenAndArchivedModesSlice> &
  ReturnType<typeof createEnvSlice> &
  ReturnType<typeof createOnlineStatusSlice> &
  ReturnType<typeof createWindowFocusSlice> &
  ReturnType<typeof createDevEnvBannerSlice> &
  ReturnType<typeof createOverviewSystemFilterSlice> &
  ReturnType<typeof createCurrentSiteSlice> &
  ReturnType<typeof createNotificationsSlice> &
  ReturnType<typeof createDrawerSlice> &
  ReturnType<typeof createReportingDiscoverySlice> &
  ReturnType<typeof createRoundsAssetsMappingsSlice>;

const hasWindow = typeof window !== "undefined";
const isLocalHost = hasWindow && window.location.hostname === "localhost";
type WindowWithReduxDevtools = Window & {
  __REDUX_DEVTOOLS_EXTENSION__?: unknown;
};

const isReduxDevtoolsAvailable =
  hasWindow && Boolean((window as WindowWithReduxDevtools).__REDUX_DEVTOOLS_EXTENSION__);
const devtoolsEnabled = isLocalHost && isReduxDevtoolsAvailable;

export const useWaterlyAppStore = createWithEqualityFn<CombinedSlices>()(
  persist(
    devtools(
      immer((...a) => ({
        ...createDataEntryDateSlice(...(a as Parameters<typeof createDataEntryDateSlice>)),
        ...createSubscriptionClientIdSlice(...(a as Parameters<typeof createSubscriptionClientIdSlice>)),
        ...createEditModeSlice(...(a as Parameters<typeof createEditModeSlice>)),
        ...createDevModeSlice(...(a as Parameters<typeof createDevModeSlice>)),
        ...createDataEntryViewSlice(...(a as Parameters<typeof createDataEntryViewSlice>)),
        ...createCurrentIntervalSlice(...(a as Parameters<typeof createCurrentIntervalSlice>)),
        ...createDataPointsSlice(...(a as Parameters<typeof createDataPointsSlice>)),
        ...createApolloClientSlice(...(a as Parameters<typeof createApolloClientSlice>)),
        ...createLoggedInUserSlice(...(a as Parameters<typeof createLoggedInUserSlice>)),
        ...createDirtyDataPointsSlice(...(a as Parameters<typeof createDirtyDataPointsSlice>)),
        ...createPerSiteLocalPrefsSlice(...(a as Parameters<typeof createPerSiteLocalPrefsSlice>)),
        ...createHiddenAndArchivedModesSlice(...(a as Parameters<typeof createHiddenAndArchivedModesSlice>)),
        ...createEnvSlice(...(a as Parameters<typeof createEnvSlice>)),
        ...createOnlineStatusSlice(...(a as Parameters<typeof createOnlineStatusSlice>)),
        ...createWindowFocusSlice(...(a as Parameters<typeof createWindowFocusSlice>)),
        ...createDevEnvBannerSlice(...(a as Parameters<typeof createDevEnvBannerSlice>)),
        ...createCurrentSiteSlice(...(a as Parameters<typeof createCurrentSiteSlice>)),
        ...createOverviewSystemFilterSlice(...(a as Parameters<typeof createOverviewSystemFilterSlice>)),
        ...createNotificationsSlice(...(a as Parameters<typeof createNotificationsSlice>)),
        ...createDrawerSlice(...(a as Parameters<typeof createDrawerSlice>)),
        ...createReportingDiscoverySlice(...(a as Parameters<typeof createReportingDiscoverySlice>)),
        ...createRoundsAssetsMappingsSlice(...(a as Parameters<typeof createRoundsAssetsMappingsSlice>)),
      })),
      { name: "waterlyZustand", enabled: devtoolsEnabled },
    ),
    {
      name: "waterly",
      partialize: (state) =>
        pick(state, [
          "editMode",
          "devMode",
          "dataEntryViews",
          "perSiteLocalPrefs",
          "showHiddenMode",
          "showArchivedMode",
          "notificationsDismissed",
          "bannerDismissedTime",
          "overviewFavoritesFilter",
          "overviewStateFilter",
        ]),
    },
  ),
  Object.is,
);
